// Waveform.jsx
//
// Shared live-audio equalizer. Originally lived inside Briefing.jsx for the
// Agenda voice flow; lifted into a component so the Goals voice flow (and
// any future voice surface) can render the exact same visual.
//
// If an AnalyserNode is supplied, we drive 13 bars from real microphone
// input via requestAnimationFrame + getByteFrequencyData. If not, we fall
// back to a CSS keyframe pulse so the UI still looks alive in pre-stream
// or post-stop states (e.g. "transcribing…").
//
// We mutate `transform: scaleY` directly on each bar instead of re-
// rendering React on every frame, and we mirror the spectrum around the
// vertical centerline (transformOrigin: 50% 50%) so it reads like a studio
// EQ rather than a column chart growing from the bottom.

import { useEffect, useRef } from 'react';

export function Waveform({ analyser }) {
  // One ref per bar. Stored in a ref-of-refs so the rAF loop can update
  // the DOM directly without triggering a React re-render every frame.
  const barRefs = useRef(Array.from({ length: 13 }, () => ({ current: null })));

  useEffect(() => {
    if (!analyser) return;

    const binCount = analyser.frequencyBinCount;
    const data = new Uint8Array(binCount);

    // Log-spaced frequency buckets. Voice energy concentrates below ~2 kHz,
    // so a linear mapping makes the right-hand bars look dead. Skip the
    // bottom couple of bins (DC + rumble) and the top ~15% (hiss / noise).
    const BARS = 13;
    const usable = Math.floor(binCount * 0.85);
    const minBin = 2;
    const ratio = Math.pow(usable / minBin, 1 / BARS);
    const edges = [];
    for (let i = 0; i <= BARS; i++) edges.push(Math.floor(minBin * Math.pow(ratio, i)));

    let raf = 0;
    const tick = () => {
      analyser.getByteFrequencyData(data);
      for (let i = 0; i < BARS; i++) {
        let sum = 0;
        let count = 0;
        for (let b = edges[i]; b < edges[i + 1] && b < binCount; b++) {
          sum += data[b];
          count++;
        }
        const avg = count > 0 ? sum / count : 0;
        // 0..255 → 0..1 with soft floor so bars never fully collapse,
        // and a mild gamma so quiet speech still registers visually.
        const norm = Math.pow(avg / 255, 0.75);
        const scaleY = 0.14 + norm * 0.86; // 14%..100% of full height
        const el = barRefs.current[i].current;
        if (el) el.style.transform = `scaleY(${scaleY.toFixed(3)})`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [analyser]);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, height: 54 }}>
      {Array.from({ length: 13 }).map((_, i) => (
        <div
          key={i}
          ref={el => (barRefs.current[i].current = el)}
          style={{
            width: 3,
            height: '100%',
            borderRadius: 3,
            background: 'var(--grad-cta)',
            boxShadow: '0 0 8px rgba(var(--accent-rgb), 0.6)',
            // Mirror symmetrically around the centerline for the studio-EQ
            // look.
            transformOrigin: '50% 50%',
            // No live analyser → fall back to the CSS pulse so the UI
            // still feels alive pre-stream / post-stop.
            animation: analyser
              ? 'none'
              : `briefWave 1.1s ease-in-out ${i * 0.075}s infinite alternate`,
            // Initial collapsed state for live mode — rAF pops it up on
            // the first frame of real audio.
            transform: analyser ? 'scaleY(0.14)' : undefined,
            transition: analyser ? 'transform 60ms linear' : undefined,
            willChange: 'transform',
          }}
        />
      ))}
    </div>
  );
}
